import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, FileText, Eye, CheckCircle, PenTool, ClipboardList, Calendar } from 'lucide-react';
import { toast } from 'react-hot-toast';
import config from '../config';

export const InstallerContractsPage = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signingId, setSigningId] = useState(null);

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl('/marketplace/contracts/'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setContracts(data.results || data);
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('خطا در دریافت قراردادها');
    } finally {
      setLoading(false);
    }
  };

  const handleSignContract = async (contractId) => {
    if (!window.confirm('آیا از امضای این قرارداد اطمینان دارید؟')) return;
    
    setSigningId(contractId);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/marketplace/contracts/${contractId}/sign/`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('قرارداد با موفقیت امضا شد');
        fetchContracts();
      } else {
        const error = await response.json();
        toast.error(error.error || 'خطا در امضای قرارداد');
      }
    } catch (error) {
      console.error('Error signing contract:', error);
      toast.error('خطا در ارتباط با سرور');
    } finally {
      setSigningId(null);
    }
  };

  const getStatusBadge = (contract) => {
    if (contract.customer_signed && contract.installer_signed) {
      return { text: 'تکمیل شده', color: 'bg-green-100 text-green-800' };
    }
    if (contract.customer_signed) {
      return { text: 'در انتظار امضای شما', color: 'bg-yellow-100 text-yellow-800' };
    }
    return { text: 'در انتظار امضای مشتری', color: 'bg-blue-100 text-blue-800' };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">قراردادهای من</h1>
            <p className="text-gray-500 text-sm mt-1">مدیریت و امضای قراردادها</p>
          </div>

          {contracts.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p>شما هیچ قراردادی ندارید</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {contracts.map((contract) => {
                const status = getStatusBadge(contract);
                const isSigning = signingId === contract.id;
                const canSign = contract.customer_signed && !contract.installer_signed;
                const isFullySigned = contract.customer_signed && contract.installer_signed;
                
                return (
                  <div key={contract.id} className="p-6 hover:bg-gray-50 transition">
                    <div className="flex flex-wrap justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <span className="font-bold text-gray-900">قرارداد #{contract.id}</span>
                          <span className={`px-2 py-1 text-xs rounded-full ${status.color}`}>
                            {status.text}
                          </span>
                        </div>
                        <div className="text-lg font-bold text-blue-600">
                          {contract.bid?.proposed_cost?.toLocaleString()} تومان
                        </div>
                        <div className="text-sm text-gray-500 mt-2">
                          {contract.terms?.substring(0, 100)}...
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                          <span>امضای مشتری: {contract.customer_signed ? '✓' : '✗'}</span>
                          <span>امضای شما: {contract.installer_signed ? '✓' : '✗'}</span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 flex-wrap">
                        <Link
                          to={`/installations/${contract.installation_request}`}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 px-3 py-2 rounded-lg border border-blue-200"
                        >
                          <Eye className="w-4 h-4" />
                          مشاهده
                        </Link>
                        
                        {/* دکمه امضا */}
                        {canSign && (
                          <button
                            onClick={() => handleSignContract(contract.id)}
                            disabled={isSigning}
                            className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            {isSigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenTool className="w-4 h-4" />}
                            امضای قرارداد
                          </button>
                        )}
                        
                        {/* دکمه زمان‌بندی - بعد از امضای کامل */}
                        {isFullySigned && (
                          <Link
                            to={`/installer/schedule/${contract.id}`}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1"
                          >
                            <Calendar className="w-4 h-4" />
                            زمان‌بندی نصب
                          </Link>
                        )}
                        
                        {/* دکمه ثبت پیشرفت */}
                        {contract.installer_signed && (
                          <Link
                            to={`/installer/progress/${contract.id}`}
                            className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 flex items-center gap-1"
                          >
                            <ClipboardList className="w-4 h-4" />
                            ثبت پیشرفت
                          </Link>
                        )}
                        
                        {/* دکمه پیگیری پروژه */}
                        {contract.installer_signed && (contract.status === 'in_progress' || contract.status === 'scheduled') && (
                          <Link
                            to={`/project/track/${contract.id}`}
                            className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 flex items-center gap-1"
                          >
                            <Eye className="w-4 h-4" />
                            پیگیری پروژه
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
